import { type FC, useEffect, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApexOptions } from 'apexcharts';
import { Box, CardBody } from 'grommet';
import { ColorType } from 'grommet/utils';
import { Card, CardProps, Text } from '/src/components';
import { ChartApiData } from '/src/lib/models/ApiData';
import theme from '/src/theme';

export const EnergyChart: FC<EnergyChartProps> = (props) => {
  /**
   * Props
   */
  const { data, height, highlightColumn = false, ...boxProps } = props;
  const [seriesData, setSeriesData] = useState<ApexAxisChartSeries>([]);

  const chartLabels = data.map((item) => item.label);

  const navigate = useNavigate();

  /* Context */
  const { t } = useTranslation();

  /* Refs */
  const themeColors = useRef(theme.global?.colors ?? {});
  const cardHeight = useRef('31.5rem');
  const chartColors = useRef<ColorType[]>([
    themeColors.current['accent-4'],
    themeColors.current['accent-5'],
    themeColors.current['brand'],
    themeColors.current['accent-6'],
    themeColors.current['accent-7'],
    themeColors.current['accent-2'],
  ]);

  const chartOptions = useRef<ApexOptions>({
    chart: {
      type: 'bar',
      fontFamily: 'Roboto Condensed, sans-serif',
      foreColor: themeColors.current['dark-3'] as string,
      toolbar: {
        show: false, // Turn off toolbar
      },
      zoom: { enabled: false }, // Must be false for CSS scrolling
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
      },
    },
    dataLabels: {
      enabled: true,
    },
    stroke: {
      show: false,
    },
    colors: chartColors.current,
    grid: {
      borderColor: '#EAECEF',
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { left: 10, right: 10 },
    },
    xaxis: {
      type: 'category',
      axisBorder: { show: false },
      crosshairs: {
        show: highlightColumn,
        width: 'tickWidth',
        position: 'back',
        opacity: 0.5,
        fill: { type: 'solid', color: '#EAECEF' },
        stroke: { width: 0 },
      },
      labels: {
        style: { fontWeight: 500 },
        hideOverlappingLabels: false,
        rotate: 0,
      },
    },
    yaxis: {
      title: { text: t('apiData:totalEnergy') },
      labels: {
        style: { fontWeight: 500, fontSize: '12px' },
        maxWidth: 200,
        formatter: (value) => value.toFixed(2),
      },
    },
    tooltip: {
      theme: 'light',
      shared: true,
      intersect: false,
      style: { fontSize: '13px' },
    },
    legend: {
      show: false,
    },
  });

  /* Effects */
  useEffect(() => {
    if (!data) {
      return;
    }

    // order labels

    //process

    chartOptions.current = {
      ...chartOptions.current,
      chart: {
        ...chartOptions.current.chart,
      },
    };

    const seriesDataMap: Record<string, number[]> = {};
    data.forEach((item, index) => {
      Object.entries(item.data).forEach(([stationName, value]) => {
        if (!seriesDataMap[stationName]) {
          seriesDataMap[stationName] = new Array(data.length).fill(0);
        }
        seriesDataMap[stationName][index] = value;
      });
    });

    setSeriesData(
      Object.entries(seriesDataMap).map(([name, data]) => ({
        name,
        data,
      }))
    );
  }, [data, navigate]);

  const dynamicWidth = chartLabels.length * 60;

  const legendFormatter = (value: string | undefined) => {
    if (!value) return value;
    const maxLength = 20;
    if (value.length > maxLength) {
      return value.substring(0, maxLength) + '...';
    }
    return value;
  };

  /* Render */
  return (
    <Card flex="grow" height={height || cardHeight.current} {...boxProps}>
      <CardBody pad={{ horizontal: 'medium', bottom: '2rem' }}>
        {/* 1. The Scrollable Viewport */}
        <Box
          width="100%"
          flex="grow"
          direction="row"
          style={{
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          <Box
            style={{
              minWidth: `max(100%, ${dynamicWidth}px)`,
              width: '100%',
              height: '100%',
            }}
          >
            <Chart
              options={{
                ...chartOptions.current,
                xaxis: {
                  ...chartOptions.current.xaxis,
                  categories: chartLabels,
                },
                yaxis: {
                  ...chartOptions.current.yaxis,
                  title: { text: t('apiData:totalEnergy') },
                },
              }}
              series={seriesData}
              type="bar"
              width="100%"
              height="100%"
            />
          </Box>
        </Box>
        <Box direction="row" justify="center" gap="medium" style={{ flexWrap: 'wrap' }} height="70px">
          {seriesData.map((series, index) => (
            <Box key={series.name} direction="row" align="center" gap="xsmall">
              {/* Replaced standard div with your library's Box */}
              <Box
                width="12px"
                height="12px"
                round="full"
                background={chartColors.current[index % chartColors.current.length]}
              />
              <Text
                size="16px"
                // weight={500}
                color={themeColors.current['dark-3']} /* Native prop understands ColorType */
              >
                {legendFormatter(series.name)}
              </Text>
            </Box>
          ))}
        </Box>
      </CardBody>
    </Card>
  );
};

export type EnergyChartProps = CardProps & {
  data: ChartApiData;
  highlightColumn?: boolean;
};
